import { EmptyState } from '@/components/ui/EmptyState';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { selectLive, useLiveStories, type Story } from '../api';
import { StoryViewer } from '../StoryViewer';

/**
 * The route behind <c>/stories/:slug</c>.
 *
 * A route rather than local state on the home page, so a story is shareable, the
 * back button closes it, and reopening the tab restores it. The cost is that the
 * viewer must work from a cold start with no home-page state to inherit — which
 * it does, because the playlist comes from the same query either way.
 */
export function StoryPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useLiveStories();

  const stories = selectLive(data);
  const exists = stories.some((story) => story.siteSlug === slug);

  const close = useCallback(() => {
    // Always an explicit navigation home, never history.back().
    //
    // On a cold deep link there is nothing behind this entry, so going back
    // would throw the visitor out of the site entirely — onto whatever they were
    // reading before, or a blank tab.
    void navigate('/', { replace: true });
  }, [navigate]);

  // Moving between sites rewrites the URL in place. Pushing would mean the back
  // button walks backwards through every site the user skimmed, which is not
  // what anyone expects it to do.
  const onStoryChange = useCallback(
    (story: Story) => {
      void navigate(`/stories/${story.siteSlug}`, { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    // A link shared yesterday, opened today. Nothing to show and no reason to
    // leave the visitor staring at a black rectangle.
    if (!isLoading && !isError && !exists) {
      close();
    }
  }, [close, exists, isError, isLoading]);

  if (isLoading) {
    return (
      <div
        className="fixed inset-0 z-50 grid h-[100dvh] place-items-center bg-black"
        role="status"
        aria-live="polite"
      >
        <span
          aria-hidden
          className="size-8 animate-spin rounded-full border-2 border-white/30 border-t-white"
        />
        <span className="sr-only">Hikâye yükleniyor</span>
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="Hikâye yüklenemedi."
        description="Bağlantını kontrol edip tekrar deneyebilirsin."
      >
        <button type="button" className="gf-button gf-button-secondary mt-4" onClick={close}>
          Ana sayfaya dön
        </button>
      </EmptyState>
    );
  }
  if (!exists) return null;

  return (
    <StoryViewer
      stories={stories}
      initialStorySlug={slug}
      onClose={close}
      onStoryChange={onStoryChange}
    />
  );
}
